<template>
    <ContentComp
        v-if="model !== null"
        class="p-0"
        :height="50"
        :show-as-modal="true"
        @is-visible="onIsVisible"
    >
        <div>
            <ButtonComp
                :button-type="'Add'"
                @click="addParticipantID"
                >{{ $t('button.add') }}</ButtonComp
            >
        </div>

        <ListComp
            ref="listComp"
            class="overflow-auto h-100"
            v-model="model"
            :gui="useGuiStore().ParticipantId"
            :select-all="false"
            :hide-toolbar="true"
            :custom-columns="handleCustomColumns"
            :hide-statusbar="true"
            :hide-messages="true"
            :row-class-rules="rowClassRules"
            @onCellEditingStarted="onCellEditingStarted"
        />
    </ContentComp>
</template>
<script lang="ts" setup>
import { ContentComp, InputSelect, ListComp } from '@/components';
import ButtonComp from '@/components/Basic/ButtonComp.vue';
import DataSourceLookup from '@/components/Basic/DataSourceLookup.vue';
import type { GridColumnOptions } from '@/components/Grid/Classes';
import { useDialog, useKeyboardListener, useLoaders, useTaskLoader } from '@/composeables';
import { ParticipantId } from '@/objects/generated';
import WellKnown from '@/objects/generated/static/WellKnown';
import type ListCompExpose from '@/objects/ListCompExpose';
import { useCommonStoreRef, useGuiStore, useUtilStore } from '@/stores';
import { propertyof } from '@/utilities';
import { watch, ref } from 'vue';

// The vModel for the participant IDs
const model = defineModel<ParticipantId[]>({ required: true });

// We need to access the listComp methods
const listComp = ref<ListCompExpose<ParticipantId> | null>(null);

// The lookups we need ie IDTypes
const { IDTypesAsSelectLine, IDTypes } = useCommonStoreRef();

const { AddLoader, LoadState } = useTaskLoader();
useLoaders(AddLoader, ['IDTYPES']);

/**
 * Callback for when the component becomes visible or hidden
 * @param isVisible
 */
const onIsVisible = (isVisible: boolean) => {
    // we add the keyboard shortcut to add a new participant ID
    if (isVisible) {
        // We add a temporary keyboard shortcut for CTRL+INSERT to add a new time
        useKeyboardListener().addKeyboardShortcut({ alias: 'participantidaddnew', on: 'CTRL+INSERT', action: addParticipantID });
    } else {
        // We make sure we remove the keyboard shortcut when the component is not visible
        useKeyboardListener().removeKeyboardShortcut('participantidaddnew');
    }
};

const handleCustomColumns = (cols: GridColumnOptions<ParticipantId>[]) => {
    // We iterate all comums and turn off sorting and filtering for all columns except the value column
    cols.forEach((col) => {
        col.sortable = false;
        col.filterable = false;
        col.suppressFillHandle = true;
        col.editable = (row: ParticipantId) => {
            if (row.EntityState == 'Added') {
                return true;
            } else {
                const rowType = IDTypes.value?.find((r) => r.ID === row.ParticipantIDTypeID);
                return rowType?.Editable ?? true;
            }
        };

        if (col.dataField == propertyof<ParticipantId>('ParticipantIDTypeID')) {
            col.minWidth = 150;
        }
    });

    // we find the column for the participantidtypeid and change the editor to a dropdown
    const typeCol = cols.find((c) => c.dataField === propertyof<ParticipantId>('ParticipantIDTypeID'));
    if (typeCol) {
        typeCol.cellRenderer = DataSourceLookup;
        typeCol.cellRendererParams = { datasource: { mapsource: IDTypesAsSelectLine } };

        typeCol.cellEditor = InputSelect;
        typeCol.cellEditorParams = { datasource: { mapsource: IDTypesAsSelectLine } };
    }

    // We want to hide the ID column
    return cols;
};

/**
 * Callback to add a new participant ID to the list
 *
 */
const addParticipantID = async () => {
    if (model.value === null) return;

    const id = ParticipantId.FromJsValues({
        EntityState: 'Added',
        ID: useUtilStore().nextFakeId(),
        ParticipantIDTypeID: WellKnown.IDTYPE_EMIT,
        Value: '',
    });

    // we add the id to the front of the list
    model.value.unshift(id);

    // we need to redraw the list
    listComp.value?.dataUpdated();

    // We set the automatic focus and start editing the new row value
    setTimeout(() => {
        listComp.value?.startEditing(propertyof<ParticipantId>('Value'));
    }, 100);
};

/**
 * Define the row rules. We want to show the row as dirty if it is modified or added and we want to show the row as muted if the ID type is not editable
 */
const rowClassRules = [
    {
        className: 'ag-row-dirty',
        func: (data: ParticipantId) => data.EntityState === 'Modified' || data.EntityState === 'Added' || data.EntityState === 'Deleted',
    },
    {
        className: 'grid-row-muted',
        func: (data: ParticipantId) => {
            const rowType = IDTypes.value?.find((r) => r.ID === data.ParticipantIDTypeID);
            return rowType?.Editable === false;
        },
    },
];

const onCellEditingStarted = (row: ParticipantId, columnId: string, rowId?: string) => {
    // We need to make sure that the current row type is editable
    const rowType = IDTypes.value?.find((r) => r.ID === row.ParticipantIDTypeID);

    if (row.EntityState != 'Added' && (rowType == null || !rowType.Editable)) {
        // Show a popup that explains that this id type is not editable and cancel the editing
        listComp.value?.stopEditing();

        useDialog().Ok({ title: 'participantid.noteditabletitle', body: 'participantid.noteditabletext' });
    }
};

watch(
    IDTypes,
    () => {
        // We redraw the list when the IDTypes change so we can apply the row rules
        listComp.value?.dataUpdated();
    },
    { once: true },
);

defineExpose({
    /**
     * Tell the view to refres its data
     */
    dataUpdated: () => {
        listComp.value?.dataUpdated();
    },
});
</script>
